import { useLocation } from "react-router-dom";
import { HARNESS_OPTIMIZER_PATH } from "./paths";

/* Which product the auth screens brand themselves as (OPSS-487). The screens
 * are shared by the Gateway and the Harness Optimizer sibling app, and two
 * signals say which one the viewer came from:
 *
 * - `client` — a CLI names itself on /cli-login?client=… (the gateway CLI sends
 *   "gateway", the hopt CLI "harness"). Explicit, so it wins.
 * - `redirect` — RequireAuth's return path (/login?redirect=/harness…), later
 *   honored by ForwardAfterAuth. A browser visit to /harness has only this.
 *   When it points back at /cli-login, the CLI's `client` rides inside it.
 *
 * Anything else — no params, another path, an unknown client, or a malformed
 * value — is the Gateway. */

export type AuthBrand = "gateway" | "harness";

/** The product name shown after "AI21" in lockups and the tab title. */
export const PRODUCT_NAME: Record<AuthBrand, string> = {
  gateway: "Gateway",
  harness: "Harness Optimizer",
};

/* What each product's command-line tool is called, for the /cli-login consent screen.
 * Deliberately not PRODUCT_NAME: the harness product is "Harness Optimizer" but its CLI
 * is `hopt`, and the consent screen has to name the thing the user actually typed. */
export const CLI_NAME: Record<AuthBrand, string> = {
  gateway: "AI21 Gateway",
  harness: "hopt",
};

/** The brand for the current route (see authBrandFor). */
export function useAuthBrand(): AuthBrand {
  return authBrandFor(useLocation().search);
}

/* Which CLI named itself, or null when none did — the distinction authBrandFor throws
 * away by defaulting to the Gateway. Only the consent screen needs it: hopt builds from
 * before 2026-09-09 send no `client`, and naming a product there on a guess defeats the
 * screen. Where the brand only picks a logo, use authBrandFor. */
export function cliClientFor(search: string): AuthBrand | null {
  const params = new URLSearchParams(search);

  const direct = brandForClient(params.get("client"));
  if (direct) return direct;

  // The signed-out bounce parks the original query inside `redirect`.
  const target = safeDecode(params.get("redirect"));
  const nested = target?.indexOf("?") ?? -1;
  if (target === null || nested === -1) return null;

  return brandForClient(
    new URLSearchParams(target.slice(nested + 1)).get("client"),
  );
}

/** The CLI named on the current route, or null (see cliClientFor). */
export function useCliClient(): AuthBrand | null {
  return cliClientFor(useLocation().search);
}

/* A Map, not an object literal: a lookup keyed by a URL parameter must not reach the
 * prototype chain. `{}["__proto__"]` and `{}["constructor"]` are truthy, so an object
 * here would return Object.prototype or the Object constructor as if it were a brand,
 * and every Record<AuthBrand, …> the callers index with it would miss. */
const CLIENT_BRANDS = new Map<string, AuthBrand>([
  ["gateway", "gateway"],
  ["harness", "harness"],
]);

export function authBrandFor(search: string): AuthBrand {
  const params = new URLSearchParams(search);

  const fromClient = brandForClient(params.get("client"));
  if (fromClient) return fromClient;

  const target = safeDecode(params.get("redirect"));
  if (!target) return "gateway";

  if (isUnderPath(target, HARNESS_OPTIMIZER_PATH)) return "harness";

  const nested = target.indexOf("?");
  if (nested === -1) return "gateway";

  return (
    brandForClient(
      new URLSearchParams(target.slice(nested + 1)).get("client"),
    ) ?? "gateway"
  );
}

function brandForClient(client: string | null): AuthBrand | null {
  if (client === null) return null;

  return CLIENT_BRANDS.get(client) ?? null;
}

function safeDecode(raw: string | null): string | null {
  if (!raw) return null;

  try {
    return decodeURIComponent(raw);
  } catch {
    return null;
  }
}

/** `/harness`, `/harness/…` or `/harness?…` — but not `/harnessx`. */
function isUnderPath(target: string, base: string): boolean {
  if (!target.startsWith(base)) return false;

  const next = target.charAt(base.length);
  return next === "" || next === "/" || next === "?";
}
